import type { SimWorld } from '@battle/sim';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import * as THREE from 'three/webgpu';
import { loadCreature } from './creatures/creature-model.ts';
import { type Creature, createKilnbackPlaceholder } from './creatures/kilnback-placeholder.ts';
import { createGame, type Game } from './game/game.ts';
import { createOnlineGame } from './game/online.ts';
import { connectPing, type PingStatus } from './net/ping.ts';
import { pickTier, qualityFor } from './render/quality.ts';
import { createPipeline, createRenderer, createResolutionController } from './render/renderer.ts';
import { createHud } from './ui/hud.ts';
import { createPlayHud } from './ui/play-hud.ts';
import { createTuningPanel } from './ui/tuning-panel.ts';
import { buildSunkenGrove } from './world/sunken-grove.ts';

declare global {
  interface Window {
    /** Read by the Playwright smoke test. */
    __battle?: {
      frames: number;
      backend: string;
      tier: string;
      ready: boolean;
      creatures?: Record<string, string>;
    };
    /** Play-mode hooks for QA scripts and e2e: the sim world and a camera override. */
    __battleDebug?: {
      world: SimWorld;
      camera: THREE.PerspectiveCamera;
      scene: THREE.Scene;
      setCameraYaw(yaw: number): void;
    };
  }
}

const BUILD = import.meta.env.VITE_BUILD_SHA ?? 'dev';

async function main() {
  const params = new URLSearchParams(location.search);
  const quality = qualityFor(pickTier(params), params);
  const hud = createHud(document.getElementById('hud') as HTMLElement);
  const container = document.getElementById('app') as HTMLElement;

  const { renderer, backend } = await createRenderer(
    container,
    quality,
    params.has('webgl') || !('gpu' in navigator),
  );
  const backendLabel = params.get('fallback') ? `${backend} (fallback)` : backend;
  window.__battle = { frames: 0, backend, tier: quality.tier, ready: false };
  const disarmCanary = installWebGPUCanary(backend, params);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 0.1, 2000);
  camera.position.set(1.4, 2.1, 7.6);

  const world = buildSunkenGrove(scene, renderer, quality, {
    noEnv: params.has('noenv'),
    noFog: params.has('nofog'),
  });
  const loaded: Record<string, string> = {};
  window.__battle.creatures = loaded;
  const creatures: Creature[] = [];
  let game: Game | null = null;
  let controls: OrbitControls | null = null;
  const lookTest = params.has('look');
  document.body.dataset.mode = lookTest ? 'look' : 'play';

  if (lookTest) {
    // M0 look test: orbit camera around a creature face-off. ?placeholder keeps the stand-in.
    const placeholder = createKilnbackPlaceholder();
    placeholder.root.rotation.y = -0.5;
    scene.add(placeholder.root);
    creatures.push(placeholder);
    loaded.kilnback = 'placeholder';
    if (!params.has('placeholder')) {
      const clip = params.get('clip');
      const spawn = [
        { id: 'kilnback', x: -1.9, z: 0.3, yaw: 1.25 },
        { id: 'rillwing', x: 2.1, z: -0.6, yaw: -1.85 },
      ];
      for (const s of spawn) {
        loadCreature(renderer, s.id)
          .then((c) => {
            c.root.position.set(s.x, 0, s.z);
            c.root.rotation.y = s.yaw;
            if (clip) c.play(clip, 0);
            scene.add(c.root);
            creatures.push(c);
            if (s.id === 'kilnback') {
              scene.remove(placeholder.root);
              creatures.splice(creatures.indexOf(placeholder), 1);
            }
            loaded[s.id] = 'model';
          })
          .catch((err: unknown) => {
            loaded[s.id] = 'failed';
            console.warn(`creature ${s.id} failed to load; keeping the stand-in`, err);
          });
      }
    }
    controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0.1, 1.0, -0.2);
    controls.enableDamping = true;
    controls.autoRotate = !params.has('still');
    controls.autoRotateSpeed = 0.35;
    controls.minDistance = 3;
    controls.maxDistance = 30;
    controls.maxPolarAngle = Math.PI * 0.49;
    controls.update();
  } else {
    // M1 playable: the sim drives the player's creature; the other one is a training dummy.
    const playHud = createPlayHud(document.getElementById('play-hud') as HTMLElement);
    const online = params.has('room') || params.has('online');
    (online ? createOnlineGame : createGame)({
      scene,
      camera,
      renderer,
      canvas: renderer.domElement,
      params,
    })
      .then((g) => {
        game = g;
        loaded.kilnback = loaded.rillwing = 'model';
        if (!g.online) createTuningPanel(g, params.has('tune'));
        playHudTick = () => playHud.render(g.status());
      })
      .catch((err: unknown) => {
        loaded.kilnback = loaded.rillwing = 'failed';
        console.error('game failed to start', err);
        playHud.error(err instanceof Error ? err.message : String(err));
      });
  }
  let playHudTick: (() => void) | null = null;

  const pipeline = createPipeline(renderer, scene, camera, quality);
  const resolution = createResolutionController(renderer, quality.maxPixelRatio);

  let ping = 'connecting…';
  const gs = params.get('gs') ?? 'gs-1';
  connectPing(gs, (s: PingStatus) => {
    ping =
      s.state === 'online' && s.rttMs !== null
        ? `${s.server ?? gs} · ${s.rttMs.toFixed(0)} ms`
        : s.state === 'online'
          ? `${s.server ?? gs} · …`
          : s.state;
  });

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });

  setTimeout(() => document.getElementById('title-card')?.classList.add('faded'), 4000);

  const timer = new THREE.Timer();
  let fps = 60;
  renderer.setAnimationLoop((now: number) => {
    timer.update(now);
    const dt = Math.min(timer.getDelta(), 0.1);
    const t = timer.getElapsed();
    fps = fps * 0.95 + (dt > 0 ? 1 / dt : 60) * 0.05;
    resolution.tick(dt);
    controls?.update(dt);
    game?.update(dt, t);
    world.update(dt, t);
    for (const c of creatures) c.update(dt, t);
    pipeline.render();

    const state = window.__battle;
    if (state) {
      state.frames++;
      if (state.frames === 30) {
        state.ready = true;
        document.body.dataset.ready = 'true';
      }
      if (state.frames === 120) disarmCanary();
    }
    if (state && state.frames % 6 === 0) playHudTick?.();
    if (state && (state.frames === 1 || state.frames % 15 === 0)) {
      hud.render({
        backend: backendLabel,
        tier: quality.tier,
        fps,
        resolution: `${Math.round(window.innerWidth * resolution.ratio)}×${Math.round(window.innerHeight * resolution.ratio)}`,
        ping,
        build: BUILD,
      });
    }
  });
}

/**
 * WebGPU canary: if the WebGPU backend throws in its first frames (driver bugs, or browser
 * builds whose WebGPU IDL differs from what three.js expects), reload once on WebGL2.
 */
function installWebGPUCanary(backend: string, params: URLSearchParams): () => void {
  if (backend !== 'WebGPU' || params.has('fallback')) return () => {};
  const isGpuError = (msg: string) => /GPU|WebGPU|WGSL|shader/i.test(msg);
  const fallback = () => {
    const url = new URL(location.href);
    url.searchParams.set('webgl', '');
    url.searchParams.set('fallback', 'webgpu-error');
    location.replace(url);
  };
  const onError = (e: ErrorEvent) => isGpuError(String(e.message)) && fallback();
  const onRejection = (e: PromiseRejectionEvent) => isGpuError(String(e.reason)) && fallback();
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

main().catch((err: unknown) => {
  console.error(err);
  const hud = document.getElementById('hud');
  if (hud) {
    createHud(hud).render({
      backend: '—',
      tier: '—',
      fps: 0,
      resolution: '—',
      ping: '—',
      build: BUILD,
      error: `Failed to start: ${err instanceof Error ? err.message : String(err)}`,
    });
  }
});
